import { Button, Card, CardContent, CircularProgress, Divider, MenuItem, TextField, Typography } from "@material-ui/core"
import React, { useEffect, useState } from "react"
import { definirModoMaquininha, listarMaquininhas, Maquininha, mensagemDeErro, ModoTela, definirNomeDaMaquininha } from "../../services/pdvoz"
import { useSnackbar } from "../../utils/notificacoes"

/**
 * MAQUININHAS DA LOJA — em que tela CADA maquininha Stone abre.
 *
 * ⚠ POR APARELHO, e por isso fora do JSON do PDVoz (que vale pra loja inteira): um balcão fica
 * em Recebimento e as do salão no Garçom. O modo mora no cadastro da maquininha
 * (DispositivoRecebimento.modo_tela, sistemaadm) e o app o lê no login, ao abrir e a cada 30
 * minutos — não há mais escolha no próprio aparelho.
 *
 * ⚠ GRAVA NA HORA, cada linha sozinha: não depende do "Salvar configuração" do fim da tela.
 */
export const MODOS_TELA: { valor: ModoTela; rotulo: string }[] = [
    { valor: "garcom", rotulo: "Garçom — pedido por mesa" },
    { valor: "recebimento", rotulo: "Recebimento — só recebe cobrança" },
    { valor: "", rotulo: "Menu inicial (escolher na hora)" },
]

function vistoEm(m: Maquininha): string {
    if (m.online) return "online agora"
    if (!m.ultimoVistoEm) return "nunca visto"
    const d = new Date(m.ultimoVistoEm)
    return isNaN(d.getTime()) ? "" : "visto em " + d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
}

export function MaquininhasDaLoja({ idEstabelecimento }: { idEstabelecimento: number | string }) {
    const { enqueueSnackbar } = useSnackbar()
    const [lista, setLista] = useState<Maquininha[]>([])
    const [carregando, setCarregando] = useState(true)
    const [salvando, setSalvando] = useState<string | null>(null)

    const carregar = async () => {
        setCarregando(true)
        try {
            setLista(await listarMaquininhas(idEstabelecimento))
        } catch (e) {
            enqueueSnackbar("Não consegui listar as maquininhas: " + mensagemDeErro(e), { variant: "error" })
        } finally {
            setCarregando(false)
        }
    }

    useEffect(() => {
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idEstabelecimento])

    /* ⚠ SALVA AO SAIR DO CAMPO, e nao a cada tecla: uma ida ao servidor por letra digitada
       encheria a fila e faria o nome "piscar" enquanto se digita. */
    const renomear = async (m: Maquininha, nome: string) => {
        const novoNome = nome.trim()
        if (!novoNome || novoNome === (m.nome || "")) return
        setSalvando(m.id)
        try {
            const nova = await definirNomeDaMaquininha(idEstabelecimento, m.id, novoNome)
            setLista(l => l.map(x => (x.id === m.id ? { ...x, ...nova } : x)))
            enqueueSnackbar(`Maquininha renomeada para "${novoNome}".`, { variant: "success" })
        } catch (e) {
            enqueueSnackbar("Não consegui renomear: " + mensagemDeErro(e), { variant: "error" })
        } finally {
            setSalvando(null)
        }
    }

    const trocar = async (m: Maquininha, modo: ModoTela) => {
        setSalvando(m.id)
        try {
            const nova = await definirModoMaquininha(idEstabelecimento, m.id, modo)
            setLista(l => l.map(x => (x.id === m.id ? { ...x, ...nova } : x)))
            enqueueSnackbar(`${m.nome || "Maquininha"}: vale na próxima abertura do app.`, { variant: "success" })
        } catch (e) {
            enqueueSnackbar("Não consegui trocar o modo: " + mensagemDeErro(e), { variant: "error" })
        } finally {
            setSalvando(null)
        }
    }

    return (
        <Card style={{ marginBottom: 16 }}>
            <CardContent>
                <Typography variant="h6">Maquininhas da loja</Typography>
                <Divider style={{ margin: "8px 0 12px" }} />
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                    A tela em que cada maquininha Stone abre. Cada uma aparece aqui depois do primeiro login no app.
                    A troca chega à maquininha no login, ao abrir o app e a cada 30 minutos, e vale na próxima vez
                    que o app abrir.
                </Typography>

                {carregando ? (
                    <CircularProgress size={24} />
                ) : !lista.length ? (
                    <Typography variant="body2" color="textSecondary">
                        Nenhuma maquininha registrada ainda. Faça login no app da maquininha para ela aparecer aqui.
                    </Typography>
                ) : (
                    lista.map(m => (
                        <div
                            key={m.id}
                            style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", marginBottom: 12 }}
                        >
                            <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                                <TextField
                                    label="Nome"
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    defaultValue={m.nome || ""}
                                    disabled={salvando === m.id}
                                    onBlur={e => renomear(m, e.target.value)}
                                    helperText={m.nome ? undefined : "Sem nome — batize para reconhecê-la"}
                                />
                                <Typography variant="caption" color="textSecondary" style={{ wordBreak: "break-all" }}>
                                    {vistoEm(m)} · ID {m.id}
                                </Typography>
                            </div>
                            <TextField
                                select
                                label="Abre em"
                                size="small"
                                variant="outlined"
                                style={{ flex: "0 1 280px", minWidth: 220 }}
                                value={m.modoTela ?? "garcom"}
                                disabled={salvando === m.id}
                                onChange={e => trocar(m, e.target.value as ModoTela)}
                                helperText={m.modoTela === undefined ? "Ainda no padrão do aparelho" : undefined}
                            >
                                {MODOS_TELA.map(o => (
                                    <MenuItem key={o.valor || "menu"} value={o.valor}>
                                        {o.rotulo}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>
                    ))
                )}

                <Button size="small" onClick={carregar} disabled={carregando}>
                    Atualizar lista
                </Button>
            </CardContent>
        </Card>
    )
}
